import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, CreateContact } from '../api/client';

export default function NewLead() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<CreateContact>({
    name: '',
    company: '',
    amazonStoreUrl: '',
    email: '',
    phone: '',
    channel: 'email',
    sourceNotes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return; // Prevenir clics múltiples
    setError('');
    setLoading(true);

    try {
      // Limpiar campos vacíos
      const data = {
        ...form,
        amazonStoreUrl: form.amazonStoreUrl || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        company: form.company || undefined,
        sourceNotes: form.sourceNotes || undefined,
      };
      
      const response = await api.contacts.create(data);
      navigate(`/contacts/${response.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <Link to="/contacts" className="text-gray-400 hover:text-gray-600 shrink-0">
          ← Volver
        </Link>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">➕ Nuevo Lead</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <p className="text-gray-500 mb-6">
          Formulario rápido para capturar leads durante tu bloque de prospección.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre * <span className="text-gray-400">(obligatorio)</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
              placeholder="Nombre del contacto"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Empresa / Tienda Amazon
            </label>
            <input
              type="text"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
              placeholder="Nombre de la empresa o tienda"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL Tienda Amazon
            </label>
            <input
              type="url"
              value={form.amazonStoreUrl}
              onChange={(e) => setForm({ ...form, amazonStoreUrl: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
              placeholder="https://www.amazon.com/stores/..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                placeholder="email@ejemplo.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
                placeholder="+1 234 567 890"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Canal de contacto
            </label>
            <select
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value as any })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
            >
              <option value="email">📧 Email</option>
              <option value="dm">💬 DM</option>
              <option value="llamada">📞 Llamada</option>
              <option value="referido">👥 Referido</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ¿Por qué lo contactaste? (Notas)
            </label>
            <textarea
              value={form.sourceNotes}
              onChange={(e) => setForm({ ...form, sourceNotes: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amazon-orange focus:border-transparent outline-none"
              placeholder="Ej: Vi que sus bullets no tienen keywords, su listing tiene fotos de baja calidad..."
              rows={3}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amazon-orange text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50"
          >
            {loading ? 'Guardando...' : '🎯 Guardar Lead'}
          </button>
        </form>
      </div>
    </div>
  );
}
